import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { TableRow } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import TerminationAuditRow from './TerminationAuditRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ offenders }) =>
  offenders.map(offender => (
    <TerminationAuditRow key={offender.contractId} {...offender} />
  ));

Tbody.propTypes = {
  offenders: PropTypes.array.isRequired
};

const TerminationAuditTable = ({
  offenders,
  count,
  limit,
  offset,
  handleChangePage,
  loading,
  ...rest
}) => {
  const { t } = useTranslation();

  const theads = useMemo(
    () => () => (
      <TableRow sx={{ '& .MuiTableCell-head': { whiteSpace: 'nowrap' } }}>
        <TableCell variant="head" key="folio" align="left">
          {t('CAPITAL_BALANCE.FOLIO')}
        </TableCell>
        <TableCell variant="head" key="status" align="left">
          {t('COMMON.STATUS')}
        </TableCell>
        <TableCell variant="head" key="terminationDate" align="left">
          {t('CAPITAL_BALANCE.TERMINATION_DATE')}
        </TableCell>
        <TableCell variant="head" key="term" align="center">
          {t('CAPITAL_BALANCE.TERM_CONTRACT_VS_DATES')}
        </TableCell>
        <TableCell variant="head" key="finalBalance" align="right">
          {t('CAPITAL_BALANCE.FINAL_BALANCE')}
        </TableCell>
      </TableRow>
    ),
    [t]
  );

  const tbody = useMemo(() => () => <Tbody offenders={offenders} />, [
    offenders
  ]);

  const page = useMemo(() => Math.floor(offset / limit) || 0, [
    offset,
    limit
  ]);

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={limit}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
      />
    ),
    [page, count, limit, handleChangePage]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

TerminationAuditTable.propTypes = {
  offenders: PropTypes.array.isRequired,
  count: PropTypes.number,
  limit: PropTypes.number.isRequired,
  offset: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool
};

TerminationAuditTable.defaultProps = {
  count: 0,
  offset: 0,
  loading: false
};

export default TerminationAuditTable;
